<template>
  <div class="mark-down-body">
    <div v-if="loading" class="mark-down-body__loading">正在加载中...</div>
    <div v-else-if="loadError" class="mark-down-body__error">
      <span class="mark-down-body__error-text">内容加载失败</span>
      <button class="mark-down-body__retry" type="button" @click="retryLoad">重试</button>
    </div>
    <div v-else class="mark-down-body__inner">
      <markdown-view :show-string="markdownContent"></markdown-view>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import MarkdownView from '@/components/markdown-view/index.vue'

defineOptions({
  name: 'MarkDownBody'
})

const props = defineProps({
  panelData: {
    type: Object,
    default: () => ({})
  }
})

const fileUrl = computed(() => props.panelData.fileUrl || '')

// markdown 原文：由 panelData.fileUrl 拉取后填入 markdown-view
const markdownContent = ref('')
const loading = ref(false)
const loadError = ref(false)
// 自增序号，用于丢弃「已切换到另一个文件」的过期请求结果
let loadSequence = 0

/**
 * 拉取 markdown 链接内容，交给 markdown-view 渲染。
 * 接口只给文件链接，正文需要前端自行 fetch。
 * @param {string} url 文件下载链接
 */
const loadMarkdownContent = async (url) => {
  if (!url) {
    markdownContent.value = ''
    loading.value = false
    loadError.value = false
    return
  }

  const sequence = ++loadSequence
  loading.value = true
  loadError.value = false
  try {
    const res = await fetch(url, { credentials: 'omit', referrerPolicy: 'no-referrer' })
    if (!res.ok) throw new Error(`load markdown failed with status ${res.status}`)
    const text = await res.text()
    // 期间切换到其他文件时，丢弃本次过期结果
    if (sequence !== loadSequence) return
    markdownContent.value = text || ''
    loading.value = false
  } catch (error) {
    console.error('[mark-down-body] load markdown failed', error)
    if (sequence !== loadSequence) return
    markdownContent.value = ''
    loading.value = false
    loadError.value = true
  }
}

const retryLoad = () => {
  loadMarkdownContent(fileUrl.value)
}

watch(
  fileUrl,
  (url) => {
    loadMarkdownContent(url)
  },
  { immediate: true }
)
</script>

<style lang="scss" scoped>
.mark-down-body {
  width: 100%;
  height: 100%;

  &__inner {
    max-width: 800px;
    padding: 0 24px;
    margin: 0 auto;
  }

  &__loading,
  &__error {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    padding: 24px;
    color: #858e9f;
    font-family: 'PingFang SC', sans-serif;
    font-size: 13px;
    font-weight: 400;
    line-height: 20px;
    box-sizing: border-box;
  }

  &__error-text {
    margin-right: 12px;
  }

  &__retry {
    padding: 4px 12px;
    border: 1px solid #e2e5eb;
    border-radius: 8px;
    background: #ffffff;
    color: #242832;
    font-family: 'PingFang SC', sans-serif;
    font-size: 13px;
    line-height: 20px;
    cursor: pointer;

    &:hover {
      background: #f5f6f8;
    }
  }
}
</style>
